import {
	CartContext as CartContextType,
	CartNasSelectedTraveller,
	CartPrices,
	CartSegment,
	NasGroupedServicesByCategory
} from './types';
import { API_DATE_FORMAT, getTravellerName, getTravellersMap } from '../utils';
import {
	CheckoutOrderFragment,
	NASProductCategory,
	NASStatus,
	PassengerFragment,
	SegmentWithTransferFragment,
	TravellerFieldEnum
} from '@websky/graphql';
import { parse } from 'date-fns';

export const groupNasServices = (
	services: CheckoutOrderFragment['additionalServices']['nas'],
	travellers: PassengerFragment[],
	flightSegments: SegmentWithTransferFragment[]
): NasGroupedServicesByCategory[] => {
	const groupedServices = new Map<NASProductCategory, CartNasSelectedTraveller[]>();

	services?.selectedServices?.forEach(selected => {
		const service = services.services?.find(service => service.id === selected.product.id);

		if (!service) {
			return;
		}

		const segment = flightSegments.find(segment => segment.segment.id === service.segmentId.toString());

		groupedServices.set(selected.product.category, [
			...(groupedServices.get(selected.product.category) ?? []),
			...selected.travellers.map(traveller => ({
				category: selected.product.category,
				productId: selected.product.id,
				name: service.name,
				date: parse(segment.segment.departure.date, API_DATE_FORMAT, new Date()),
				segmentIata: segment.segment.departure.airport.iata,
				travellerName: getTravellerName(travellers.find(t => t.id === traveller.id)),
				travellerId: traveller.id,
				totalPrice: selected.fare.prices.find(price => price.traveller.id === traveller.id)?.price,
				canBeDeleted: selected.status === NASStatus.New
			}))
		]);
	});

	return [...groupedServices.entries()].map(([category, products]) => ({
		category,
		travellers: products.sort((a, b) => a.date.getTime() - b.date.getTime())
	}));
};

export const createCartContext = (
	travellers: PassengerFragment[],
	segments: SegmentWithTransferFragment[],
	prices: CartPrices
): CartContextType => {
	const segmentsById = new Map<string, CartSegment>();

	segments.forEach(({ segment }) => {
		segmentsById.set(segment.id, {
			id: segment.id,
			iataDirection: `${segment.departure.airport.iata} — ${segment.arrival.airport.iata}`
		});
	});

	return {
		travellersById: getTravellersMap(travellers),
		segmentsById,
		totalPrices: {
			baggage: prices?.baggage,
			flights: prices?.airticket,
			seats: prices?.seats,
			meal: prices?.meals,
			total: prices?.total,
			aeroexpress: prices?.aeroexpress,
			insurance: prices?.insurance,
			businessLounge: prices?.businessLounge,
			refundableTicket: prices?.refundableTicket,
			animals: prices?.animals,
			transfer: prices?.transfer,
			priorityBoarding: prices?.priorityBoarding,
			baggageDelivery: prices?.baggageDelivery,
			sms: prices?.sms,
			nas: prices?.nas
		}
	};
};

export const getPassengerValuesMap = (traveller: PassengerFragment) => {
	const valuesMap = new Map<TravellerFieldEnum, string>();

	traveller.values.forEach(value => {
		valuesMap.set(value.type, value.value);
	});

	return valuesMap;
};
